import type { CSSProperties } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut, Sun, Moon, Menu, Boxes, MonitorDot, Rocket, BarChart3, Settings, type LucideIcon } from 'lucide-react';
import { useAuthStore, StaffRole } from '@/stores/auth.store';
import { signOut } from '@/services/session';
import { useGameStore } from '@/stores/game.store';
import { type FeatureKey } from '@/config/games';
import { useThemeStore } from '@/stores/theme.store';
import { spaceFromPath, type PanelSpace } from '@/stores/space.store';
import { GameSwitcher } from './GameSwitcher';
import { GameGroupSwitcher } from './GameGroupSwitcher';
import { useActiveGameGroup } from '@/stores/gameGroups.store';
import { useModuleEnabled } from '@/stores/projectPages.store';
import { moduleRoutesOfSpace, opensWhenOff } from '@/config/panelModules';
import { useSettingsSections } from '@/config/settingsSections';
import { usesGameGroup } from '@/utils/gameGroups';
import { useSupervisionLanding } from './supervisionNav';
import { DeployHeaderControls } from '@/deploy/components/layout/DeployHeaderControls';
import styles from './Header.module.scss';

interface SpaceTab {
  id: PanelSpace;
  label: string;
  icon: LucideIcon;
  path: string;
  /** Réservé aux admins. */
  adminOnly?: boolean;
  /** Clé de page du grade : sans elle, l'onglet mènerait à un écran refusé. */
  page?: string;
  /** Fonctionnalité du jeu actif : tous les projets n'exposent pas les mêmes écrans. */
  feature?: FeatureKey;
  /**
   * Espace fait de modules (Paramètres généraux > Fonctionnalités) : son onglet mène au premier
   * module actif pour le projet actif que le grade peut ouvrir, et il est masqué quand il n'y en a
   * aucun. Supervision n'en est pas : le Dashboard et Grades & accès n'y sont pas des modules.
   */
  modular?: boolean;
  /**
   * Accueil calculé (Supervision) : le Dashboard, ou la première page ouverte quand le satellite du
   * groupe actif ne sert aucune carte du Dashboard (cf useSupervisionLanding).
   */
  landing?: boolean;
}

const SPACES: SpaceTab[] = [
  { id: 'supervision', label: 'Supervision', icon: MonitorDot, path: '/dashboard', landing: true },
  // Plus de page hors module en Configuration : Pack, Fichiers et Containers sont dans les
  // Paramètres généraux. L'onglet mène au premier module de contenu actif et ouvert au grade.
  { id: 'configuration', label: 'Configuration', icon: Boxes, path: '/config/blocks', modular: true },
  { id: 'deploiement', label: 'Déploiement', icon: Rocket, path: '/deploiement', adminOnly: true },
  // Statistiques était une entrée de la barre latérale de Supervision. Promu en espace, il garde
  // EXACTEMENT le gating qu'avait cette entrée (page 'stats' + feature 'stats') : sans ça, l'onglet
  // s'afficherait pour des staff qui n'ont pas le droit et tomberait sur un écran refusé.
  { id: 'statistiques', label: 'Statistiques', icon: BarChart3, path: '/stats', page: 'stats', feature: 'stats', modular: true },
];

export function Header({ onMenuClick }: { onMenuClick?: () => void }) {
  const { user, hasPage } = useAuthStore();
  const hasFeature = useGameStore((s) => s.hasFeature);
  const resolved = useThemeStore((s) => s.resolved);
  const toggleTheme = useThemeStore((s) => s.toggle);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Espace dérivé de l'URL (source de vérité unique) → onglet toujours cohérent avec la route.
  const space = spaceFromPath(pathname);
  // Groupe de jeu consulté (NationsGlory : Nova, Iris), null pour un jeu sans groupes (Paladium) :
  // sur une page qui lit le jeu, liseré du bandeau à sa couleur, et bascule mise en avant en
  // Supervision et Statistiques.
  const gameGroup = useActiveGameGroup();
  const groupedPage = !!gameGroup && usesGameGroup(pathname);
  const emphasizeGroup = space === 'supervision' || space === 'statistiques';

  const isAdmin = !!user?.isAdmin || user?.role === StaffRole.ADMIN;
  const isModuleOn = useModuleEnabled();
  // Sections des Paramètres généraux ouvertes au grade : l'écrou s'affiche dès qu'il y en a une.
  const settingsSections = useSettingsSections();
  const supervisionLanding = useSupervisionLanding();

  // Cible de l'onglet : son accueil, ou pour un espace fait de modules, le premier module actif du
  // projet que le grade peut ouvrir (Configuration sans Blocks s'ouvre sur le module suivant, pas
  // sur un écran fermé ni refusé). Aucun : null, l'onglet est masqué.
  const targetOf = (tab: SpaceTab): string | null => {
    if (tab.landing) return supervisionLanding;
    if (!tab.modular) return tab.path;
    const routes = moduleRoutesOfSpace(tab.id, isAdmin)
      .filter((r) => isModuleOn(r.module)
        && (!r.pages || r.pages.some((p) => hasPage(p)) || opensWhenOff(r.pagesWhenOff, hasPage, isModuleOn)));
    if (routes.length === 0) return null;
    return routes.some((r) => r.path === tab.path) ? tab.path : routes[0]!.path;
  };

  const goToSpace = (target: SpaceTab) => {
    const path = targetOf(target);
    // No-op seulement si on est DÉJÀ sur l'accueil de l'espace ; sinon on y (re)vient,
    // y compris depuis un sous-écran de l'espace courant (ex. /config/items → /config/blocks).
    if (!path || path === pathname) return;
    navigate(path);
  };

  // Même règle que la barre latérale : un onglet ne s'affiche que si l'écran derrière est
  // réellement accessible. Un onglet visible qui mène à « accès refusé » ou à un module coupé pour
  // ce projet est pire qu'absent.
  const visibleSpaces = SPACES.filter((s) =>
    (!s.adminOnly || isAdmin) &&
    (!s.page || hasPage(s.page)) &&
    (!s.feature || hasFeature(s.feature)) &&
    targetOf(s) !== null);

  // Position du curseur coulissant. Calculée sur les onglets RÉELLEMENT visibles, et non codée en
  // dur par identifiant dans le SCSS : sinon un nouvel espace n'a pas de règle et le curseur reste
  // sur le premier, et un espace masqué (Déploiement pour un non-admin) décale tous les suivants.
  // rawIndex = -1 quand l'espace courant n'a pas d'onglet (Paramètres, via l'écrou) : le curseur
  // est alors masqué au lieu de s'accrocher à tort au premier onglet.
  const rawIndex = visibleSpaces.findIndex((s) => s.id === space);
  const activeIndex = Math.max(0, rawIndex);

  return (
    <header
      className={groupedPage ? `${styles.header} ${styles.headerGrouped}` : styles.header}
      style={groupedPage && gameGroup?.color ? { '--game-group-color': gameGroup.color } as CSSProperties : undefined}
    >
      <div className={styles.left}>
        <button className={styles.menuButton} onClick={onMenuClick} aria-label="Menu">
          <Menu size={22} strokeWidth={2} />
        </button>
        <GameSwitcher />
        <div
          className={styles.spaceSwitch}
          data-space={space}
          style={{ '--space-count': visibleSpaces.length, '--space-index': activeIndex } as CSSProperties}
          role="tablist"
          aria-label="Espace du panel"
        >
          <div className={styles.spaceSwitchThumb} style={rawIndex < 0 ? { opacity: 0 } : undefined} />
          {visibleSpaces.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={space === s.id}
                className={`${styles.spaceSwitchItem} ${space === s.id ? styles.spaceSwitchItemActive : ''}`}
                onClick={() => goToSpace(s)}
              >
                <Icon size={15} strokeWidth={2} />
                <span className={styles.spaceSwitchLabel}>{s.label}</span>
              </button>
            );
          })}
        </div>
        {/* Après les espaces : le sélecteur n'existe pas sur toutes les pages, placé avant il
            décalait les onglets d'une page à l'autre. */}
        <GameGroupSwitcher emphasis={emphasizeGroup} />
      </div>

      <div className={styles.right}>
        {space === 'deploiement' && <DeployHeaderControls />}

        {/* Paramètres généraux : réglages du panel (admins globaux) et Pack / Fichiers / Containers du
            projet actif (droits de grade). Visible dès qu'une section est ouverte. */}
        {settingsSections.length > 0 && (
          <button
            className={`${styles.themeToggle} ${space === 'parametres' ? styles.settingsActive : ''}`}
            onClick={() => navigate('/parametres')}
            title="Paramètres généraux"
            aria-label="Paramètres généraux"
          >
            <Settings size={18} strokeWidth={2} />
          </button>
        )}

        <button
          className={styles.themeToggle}
          onClick={toggleTheme}
          title={resolved === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
          aria-label="Changer de thème"
        >
          {resolved === 'dark' ? <Sun size={18} strokeWidth={2} /> : <Moon size={18} strokeWidth={2} />}
        </button>

        {user && (
          <div className={styles.userMenu}>
            <span className={styles.gamertag}>{user.gamertag}</span>
            <button className={styles.logoutBtn} onClick={signOut}>
              <LogOut size={16} strokeWidth={2} />
              <span>Déconnexion</span>
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
